import '../scss/layout/priority-carousel.scss';

export function normalizePriorityItems(items, origin) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    try {
      const url = new URL(item.url);
      const image = new URL(item.imageUrl);
      if (url.origin !== origin || url.protocol !== 'https:' || url.username || url.password ||
          image.protocol !== 'https:' || image.hostname !== 'storage.ghost.io' || image.username || image.password ||
          typeof item.title !== 'string' || !item.title.trim() || seen.has(url.pathname)) return false;
      seen.add(url.pathname);
      return true;
    } catch { return false; }
  }).slice(0, 4);
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

function loadImage(image) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 8000);
    const done = (ok) => { clearTimeout(timeout); resolve(ok); };
    if (image.complete) return done(image.naturalWidth > 0);
    image.onload = () => done(image.naturalWidth > 0);
    image.onerror = () => done(false);
  });
}

export async function initPriorityCarousel() {
  // Temporary Danish Discover test: retain the server-rendered latest post.
  if (window.location.hostname === 'weloveapple.dk') return;
  const root = document.querySelector('[data-wla-priority-carousel]');
  if (!root || root.dataset.carouselReady) return;
  // The server-rendered latest article remains available without JS or the feed.
  try {
    const response = await fetch('/api/hero?limit=4', {
      headers: { accept: 'application/json' }, signal: AbortSignal.timeout(8000)
    });
    if (!response.ok) return;
    const data = await response.json();
    const items = normalizePriorityItems(data.items, window.location.origin);
    if (!items.length) return;

    const carousel = element('section', 'wla-priority-carousel');
    carousel.setAttribute('aria-label', 'Fremhævede artikler');
    carousel.setAttribute('aria-roledescription', 'karrusel');
    const viewport = element('div', 'wla-priority-carousel__viewport');
    const slides = items.map((item, index) => {
      const slide = element('article', 'wla-priority-carousel__slide');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'dias');
      slide.setAttribute('aria-label', `${index + 1} af ${items.length}`);
      slide.hidden = index !== 0;
      const img = element('img', 'wla-priority-carousel__image');
      img.alt = item.imageAlt || '';
      img.decoding = 'async';
      img.src = item.imageUrl;
      const body = element('div', 'wla-priority-carousel__body');
      const label = element('span', 'wla-priority-carousel__label',
        item.source === 'selected' ? 'Redaktionen anbefaler' : item.source === 'most-read' ? 'Mest læste' : 'Seneste nyt');
      const heading = element('h2', 'wla-priority-carousel__title');
      const link = element('a', 'wla-priority-carousel__link', item.title);
      link.href = item.url;
      heading.append(link);
      body.append(label, heading);
      if (item.excerpt) body.append(element('p', 'wla-priority-carousel__excerpt', item.excerpt));
      body.append(element('span', 'wla-priority-carousel__read', 'Læs historien →'));
      slide.append(img, body);
      if (item.imageCaption) slide.append(element('small', 'wla-priority-carousel__credit', item.imageCaption));
      viewport.append(slide);
      return slide;
    });
    const loaded = await loadImage(slides[0].querySelector('img'));
    if (!loaded || root.contains(document.activeElement)) return;
    carousel.append(viewport);
    let index = 0;
    let timer;
    let userPaused = false;
    let inView = true;
    let hovering = false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controls = element('div', 'wla-priority-carousel__controls');
    const status = element('span', 'wla-priority-carousel__status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const button = (text, label, handler) => {
      const node = element('button', 'wla-priority-carousel__button', text);
      node.type = 'button';
      node.setAttribute('aria-label', label);
      node.addEventListener('click', handler);
      return node;
    };
    const syncTimer = () => {
      clearInterval(timer);
      if (slides.length > 1 && !userPaused && !reduced.matches && !document.hidden && inView &&
          !hovering && !carousel.contains(document.activeElement)) timer = setInterval(() => show(index + 1), 8000);
    };
    const show = (next, manual = false) => {
      const target = (next + slides.length) % slides.length;
      const targetImage = slides[target].querySelector('img');
      if (!targetImage.complete || !targetImage.naturalWidth) return;
      index = target;
      slides.forEach((slide, i) => { slide.hidden = i !== index; });
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (manual) {
        userPaused = true;
        status.textContent = `${index + 1} af ${slides.length}: ${items[index].title}`;
        updatePause();
      }
      syncTimer();
    };
    const previous = button('←', 'Forrige artikel', () => show(index - 1, true));
    const next = button('→', 'Næste artikel', () => show(index + 1, true));
    previous.classList.add('wla-priority-carousel__previous');
    next.classList.add('wla-priority-carousel__next');
    const dots = slides.map((slide, i) => {
      const dot = button(String(i + 1), `Vis artikel ${i + 1}: ${items[i].title}`, () => show(i, true));
      dot.classList.add('wla-priority-carousel__dot');
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      return dot;
    });
    const pause = button('Pause', 'Sæt automatisk rotation på pause', () => {
      userPaused = !userPaused;
      updatePause();
      syncTimer();
    });
    pause.classList.add('wla-priority-carousel__pause');
    const updatePause = () => {
      pause.textContent = userPaused ? 'Afspil' : 'Pause';
      pause.setAttribute('aria-label', userPaused ? 'Start automatisk rotation' : 'Sæt automatisk rotation på pause');
      pause.hidden = reduced.matches;
    };
    controls.append(previous, ...dots, next, pause);
    controls.hidden = slides.length < 2;
    carousel.append(controls, status);
    root.replaceChildren(carousel);
    root.dataset.carouselReady = 'true';
    carousel.addEventListener('mouseenter', () => { hovering = true; syncTimer(); });
    carousel.addEventListener('mouseleave', () => { hovering = false; syncTimer(); });
    carousel.addEventListener('focusin', (event) => {
      if (event.target !== pause) userPaused = true;
      updatePause(); syncTimer();
    });
    carousel.addEventListener('focusout', () => setTimeout(syncTimer, 0));
    carousel.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      show(index + (event.key === 'ArrowLeft' ? -1 : 1), true);
    });
    let touchStart;
    let suppressClickUntil = 0;
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      touchStart = {x: event.clientX, y: event.clientY};
      userPaused = true; updatePause(); syncTimer();
    });
    viewport.addEventListener('pointerup', (event) => {
      if (!touchStart) return;
      const dx = event.clientX - touchStart.x, dy = event.clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
      suppressClickUntil = Date.now() + 400;
      show(index + (dx < 0 ? 1 : -1), true);
    });
    viewport.addEventListener('pointercancel', () => { touchStart = null; });
    viewport.addEventListener('click', (event) => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, true);
    document.addEventListener('visibilitychange', syncTimer);
    reduced.addEventListener('change', () => { updatePause(); syncTimer(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; syncTimer(); }).observe(carousel);
    }
    updatePause();
    syncTimer();
  } catch { /* Keep the server-rendered hero on network/JSON failure. */ }
}
